/**
 * useActiveTimers — live query that finds all tasks with a currently running timer.
 *
 * Scans the totodo table for tasks where `timerStartedAt` is set.
 * Returns an array sorted by most recently started first.
 *
 * Performance: `timerStartedAt` is not indexed. Instead of scanning the whole
 * table (the previous db.totodo.filter() visited ~23K objects on a large
 * account, on every totodo write, and blocked readwrite transactions while it
 * ran) this reads live, not-completed tasks via [type+isDeleted+status].
 * Completed tasks are skipped safely: completing a task stops its timer
 * (taskOps completeTask -> stopTimer). An index on timerStartedAt would make
 * this near-free but needs a schema version bump on client and server.
 *
 * Each key is read with a plain equals().toArray(), which Dexie serves with a
 * single getAll(); the timer check runs in JS. Adding .filter() or anyOf()
 * makes Dexie iterate a cursor with one round trip to the main thread per row,
 * which under startup load stretched this read to ~8s — and a readwrite
 * transaction on totodo cannot start until it is done.
 */

import { useLiveQueryImmediate } from "@/db/hooks"
import { db, ObjectStatus, ObjectType } from "@/db/schema"
import type { DbObject } from "@/db/schema"

export function useActiveTimers(): DbObject[] {
  const [result] = useLiveQueryImmediate(
    async () => {
      const keys: [ObjectType, number, ObjectStatus][] = []
      for (const type of [ObjectType.Task, ObjectType.Subtask]) {
        for (const status of [ObjectStatus.Unfinished, ObjectStatus.Canceled, ObjectStatus.Hidden]) {
          keys.push([type, 0, status])
        }
      }
      const groups = await Promise.all(
        keys.map((key) =>
          db.totodo.where("[type+isDeleted+status]").equals(key).toArray(),
        ),
      )
      const tasks = groups.flat().filter((t) => !!t.timerStartedAt)
      // Most recently started first
      tasks.sort((a, b) => Number(b.timerStartedAt) - Number(a.timerStartedAt))
      return tasks
    },
    ["activeTimers"],
    [],
  )
  return result
}
